/**
 * @fileoverview Scratch extension for controlling Arduino boards via Web Serial API.
 * This extension enables real-time communication with Arduino Uno, ESP-8266, or Raspberry Pi
 * for digital/analog I/O, PWM, servo control, tone generation, and ultrasonic sensor reading.
 *
 * @author Alan Yorinks
 * @copyright Copyright (c) 2019 Alan Yorinks All rights reserved.
 * @license GNU AFFERO GENERAL PUBLIC LICENSE Version 3
 */

const ArgumentType = require("../../extension-support/argument-type");
const BlockType = require("../../extension-support/block-type");
const formatMessage = require("format-message");

/**
 * Pin mode constants matching Arduino firmware expectations.
 * These values must match the firmware running on the Arduino board.
 */
const DIGITAL_INPUT = 1; // Pin configured for digital input
const PWM = 3; // Pin configured for PWM (analog output)
const SERVO = 4; // Pin configured for servo motor control
const TONE = 5; // Pin configured for tone/buzzer output
const SONAR = 6; // Pin configured for ultrasonic sensor
const ANALOG_INPUT = 7; // Pin configured for analog input

/**
 * Configuration constants for Arduino communication and hardware.
 */
const CONFIG = {
    BAUD_RATE: 57600, // Serial communication baud rate
    CONNECTION_TIMEOUT: 1000, // Auto-connect delay in milliseconds
    MAX_DIGITAL_PINS: 52, // Maximum number of digital pins
    MAX_ANALOG_PINS: 6, // Maximum number of analog pins
    MAX_TONE_DURATION: 5000, // Maximum tone duration in milliseconds
    PWM_MAX_VALUE: 255, // Maximum PWM value (8-bit)
    PWM_PERCENTAGE_SCALE: 100, // Scale factor for percentage conversion
};

require("sweetalert");

// common
const FormDigitalWrite = {
    "pt-br": "Escrever Pino Digital [PIN]como[ON_OFF]",
    pt: "Escrever Pino Digital[PIN]como[ON_OFF]",
    en: "Write Digital Pin [PIN] [ON_OFF]",
    fr: "Mettre la pin numérique[PIN]à[ON_OFF]",
    "zh-tw": "腳位[PIN]數位輸出[ON_OFF]",
    "zh-cn": "引脚[PIN]数字输出[ON_OFF]",
    pl: "Ustaw cyfrowy Pin [PIN] na [ON_OFF]",
    de: "Setze digitalen Pin [PIN] [ON_OFF]",
    ja: "デジタル・ピン [PIN] に [ON_OFF] を出力",
    nl: "Schrijf Digitale Pin [PIN] [ON_OFF]",
};

const FormPwmWrite = {
    "pt-br": "Escrever Pino PWM[PIN]com[VALUE]%",
    pt: "Escrever Pino PWM[PIN]com[VALUE]%",
    en: "Write PWM Pin [PIN] [VALUE]%",
    fr: "Mettre la pin PWM[PIN]à[VALUE]%",
    "zh-tw": "腳位[PIN]類比輸出[VALUE]%",
    "zh-cn": "引脚[PIN]模拟输出[VALUE]%",
    pl: "Ustaw PWM Pin [PIN] na [VALUE]%",
    de: "Setze PWM-Pin [PIN] [VALUE]%",
    ja: "PWM ピン [PIN] に [VALUE]% を出力",
    nl: "Schrijf PWM Pin [PIN] [VALUE]%",
};

const FormTone = {
    "pt-br": "Soar no Pino[PIN]com[FREQ]Hz e[DURATION]ms",
    pt: "Soar no Pino[PIN]com[FREQ]Hz  e[DURATION]ms",
    en: "Tone Pin [PIN] [FREQ] Hz [DURATION] ms",
    fr: "Définir le buzzer sur la pin[PIN]à[FREQ]Hz pendant[DURATION] ms",
    "zh-tw": "腳位[PIN]播放音調，頻率為[FREQ]赫茲 時間為[DURATION]毫秒",
    "zh-cn": "引脚[PIN]播放音调，频率为[FREQ]赫兹 时间为[DURATION]毫秒",
    pl: "Ustaw brzęczyk na Pinie [PIN] na [FREQ] Hz i [DURATION] ms%",
    de: "Spiele Ton am Pin [PIN] [FREQ] Hz [DURATION] ms",
    ja: "音調ピン [PIN] を [FREQ] Hz [DURATION] ms に",
    nl: "Zet toon van pin [PIN] op [FREQ] Hz voor [DURATION] ms",
};

const FormServo = {
    "pt-br": "Mover Servo Motor no[PIN]para[ANGLE]°",
    pt: "Mover Servo Motor no[PIN]para[ANGLE]°",
    en: "Write Servo Pin [PIN] [ANGLE] Deg.",
    fr: "Mettre le servo[PIN]à[ANGLE] Deg.",
    "zh-tw": "伺服馬達腳位[PIN]轉動角度到[ANGLE]度",
    "zh-cn": "伺服电机引脚[PIN]转动角度到[ANGLE]度",
    pl: "Ustaw silnik servo na Pinie [PIN] na [ANGLE]°",
    de: "Setze Servo-Pin [PIN] [ANGLE]°",
    ja: "サーボ・ピン [PIN] に [ANGLE] 度を出力",
    nl: "Schrijf Servo Pin [PIN] [ANGLE]° graden",
};

const FormAnalogRead = {
    "pt-br": "Ler Pino Analógico [PIN]",
    pt: "Ler Pino Analógico [PIN]",
    en: "Read Analog Pin [PIN]",
    fr: "Lecture analogique [PIN]",
    "zh-tw": "讀取類比腳位[PIN]",
    "zh-cn": "读取模拟引脚[PIN]",
    pl: "Odczytaj analogowy Pin [PIN]",
    de: "Lies analogen Pin [PIN]",
    ja: "アナログ・ピン [PIN] から入力",
    nl: "Lees Analoge Pin [PIN]",
};

const FormDigitalRead = {
    "pt-br": "Ler Pino Digital [PIN]",
    pt: "Ler Pino Digital [PIN]",
    en: "Read Digital Pin [PIN]",
    fr: "Lecture numérique [PIN]",
    "zh-tw": "讀取數位腳位[PIN]",
    "zh-cn": "读取数字引脚[PIN]",
    pl: "Odczytaj cyfrowy Pin [PIN]",
    de: "Lies digitalen Pin [PIN]",
    ja: "デジタル・ピン [PIN] から入力",
    nl: "Lees Digitale Pin [PIN]",
};

const FormSonarRead = {
    "pt-br": "Ler Distância: Sonar em T[TRIGGER_PIN] E[ECHO_PIN]",
    pt: "Ler Distância: Sonar em T[TRIGGER_PIN] E[ECHO_PIN]",
    en: "Read SONAR  T [TRIGGER_PIN]  E [ECHO_PIN]",
    fr: "Distance de lecture : Sonar T [TRIGGER_PIN] E [ECHO_PIN]",
    "zh-tw": "HCSR超音波感測器，Echo在腳位[ECHO_PIN] Trig在腳位[TRIGGER_PIN]",
    "zh-cn": "HCSR超声波传感器，Echo在引脚[ECHO_PIN] Trig在引脚[TRIGGER_PIN]",
    pl: "Odczytaj odległość: Sonar T [TRIGGER_PIN]  E [ECHO_PIN]",
    de: "Lies Sonar T [TRIGGER_PIN]  E [ECHO_PIN]",
    ja: "超音波測距器からトリガ [TRIGGER_PIN] とエコー [ECHO_PIN] で入力",
    nl: "Lees SONAR T [TRIGGER_PIN] E [ECHO_PIN]",
};

/**
 * Scratch extension class for Arduino board control via Web Serial API.
 * Provides blocks for digital/analog I/O, PWM, servo control, tone generation, and sensor reading.
 */
class Scratch3ArduinoOneGPIO {
    /**
     * Create a new Arduino OneGPIO extension instance.
     * @param {Runtime} runtime - The Scratch runtime instance
     */
    constructor(runtime) {
        // Core runtime and connection state
        this.runtime = runtime;
        this.connected = false;
        this.connection_pending = false;
        this.connect_attempt = false;
        this.alerted = false;

        // Web Serial API objects
        this.serialPort = null;
        this.writer = null;
        this.reader = null;
        this.readLoopActive = false;

        // Pin state arrays - store current values and modes
        this.digital_inputs = new Array(CONFIG.MAX_DIGITAL_PINS).fill(0);
        this.analog_inputs = new Array(CONFIG.MAX_ANALOG_PINS).fill(0);
        this.pin_modes = new Array(CONFIG.MAX_DIGITAL_PINS).fill(-1);

        // Command queue and sensor state
        this.wait_open = []; // Commands waiting for connection
        this.sonar_report_pin = null; // Pin used for sonar distance reporting
        this.the_locale = null; // Current language locale

        // Auto-connect after a short delay to allow page to fully load
        setTimeout(() => {
            if (!this.connected && !this.connection_pending) {
                this.connect();
            }
        }, CONFIG.CONNECTION_TIMEOUT);
    }

    getInfo() {
        this.the_locale = this._setLocale();

        return {
            id: "onegpioArduino",
            color1: "#0C5986",
            color2: "#34B0F7",
            name: "OneGpio Arduino",
            blockIconURI:
                "data:image/png;base64,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",
            blocks: [
                {
                    opcode: "digital_write",
                    blockType: BlockType.COMMAND,
                    // text: 'Write Digital Pin [PIN] [ON_OFF]',
                    text: FormDigitalWrite[this.the_locale],

                    arguments: {
                        PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "2",
                            menu: "digital_pins",
                        },
                        ON_OFF: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "0",
                            menu: "on_off",
                        },
                    },
                },
                {
                    opcode: "pwm_write",
                    blockType: BlockType.COMMAND,
                    text: FormPwmWrite[this.the_locale],
                    arguments: {
                        PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "3",
                            menu: "pwm_pins",
                        },
                        VALUE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "50",
                        },
                    },
                },
                "---",
                {
                    opcode: "tone_on",
                    blockType: BlockType.COMMAND,
                    text: FormTone[this.the_locale],
                    arguments: {
                        PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "2",
                            menu: "digital_pins",
                        },
                        FREQ: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 100,
                        },
                        DURATION: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 50,
                        },
                    },
                },

                "---",
                {
                    opcode: "servo",
                    blockType: BlockType.COMMAND,
                    text: FormServo[this.the_locale],
                    arguments: {
                        PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "2",
                            menu: "digital_pins",
                        },
                        ANGLE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 90,
                        },
                    },
                },
                "---",
                {
                    opcode: "analog_read",
                    blockType: BlockType.REPORTER,
                    text: FormAnalogRead[this.the_locale],
                    arguments: {
                        PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "0",
                            menu: "analog_pins",
                        },
                    },
                },
                "---",
                {
                    opcode: "digital_read",
                    blockType: BlockType.REPORTER,
                    text: FormDigitalRead[this.the_locale],
                    arguments: {
                        PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "2",
                            menu: "digital_pins",
                        },
                    },
                },
                "---",
                {
                    opcode: "sonar_read",
                    blockType: BlockType.REPORTER,
                    text: FormSonarRead[this.the_locale],

                    arguments: {
                        TRIGGER_PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "7",
                            menu: "digital_pins",
                        },
                        ECHO_PIN: {
                            type: ArgumentType.NUMBER,
                            defaultValue: "8",
                            menu: "digital_pins",
                        },
                    },
                },
            ],
            menus: {
                digital_pins: {
                    acceptReporters: true,
                    items: [
                        "2",
                        "3",
                        "4",
                        "5",
                        "6",
                        "7",
                        "8",
                        "9",
                        "10",
                        "11",
                        "12",
                        "13",
                        "14",
                        "15",
                        "16",
                        "17",
                        "18",
                        "19",
                        "20",
                        "21",
                        "22",
                        "23",
                        "24",
                        "25",
                        "26",
                        "27",
                        "28",
                        "29",
                        "30",
                        "31",
                        "32",
                        "33",
                        "34",
                        "35",
                        "36",
                        "37",
                        "38",
                        "39",
                        "40",
                        "41",
                        "42",
                        "43",
                        "44",
                        "45",
                        "46",
                        "47",
                        "48",
                        "49",
                        "50",
                        "51",
                        "52",
                    ],
                },
                pwm_pins: {
                    acceptReporters: true,
                    items: ["3", "5", "6", "9", "10", "11"],
                },
                analog_pins: {
                    acceptReporters: true,
                    items: ["0", "1", "2", "3", "4", "5"],
                },

                mode: {
                    acceptReporters: true,
                    items: [
                        { text: "Input", value: "1" },
                        { text: "Output", value: "2" },
                    ],
                },
                on_off: {
                    acceptReporters: true,
                    items: ["0", "1"],
                },
            },
        };
    }

    // === BLOCK HANDLER METHODS ===
    // These methods implement the functionality for each Scratch block

    /**
     * Write a digital value (HIGH/LOW) to a pin.
     * Block implementation for "Write Digital Pin [PIN] [ON_OFF]".
     * @param {object} args - Block arguments
     * @param {string} args.PIN - Pin number to write to
     * @param {string} args.ON_OFF - Value to write (0=LOW, 1=HIGH)
     */
    async digital_write(args) {
        console.log("digital_write called", args);

        // Ensure connection is established
        if (!this.connected && !this.connection_pending) {
            await this.connect();
        }

        // Queue command if not connected yet
        if (!this.connected) {
            console.log("Still not connected, queueing command.");
            const callbackEntry = [this.digital_write.bind(this), args];
            this.wait_open.push(callbackEntry);
            return;
        }

        // Parse arguments and send command
        console.log("Connected, sending command...");
        const pin = parseInt(args["PIN"], 10);
        const value = parseInt(args["ON_OFF"], 10);

        await this.sendSerial(
            JSON.stringify({ command: "digital_write", pin, value })
        );
        console.log("Command sent!");
    }

    /**
     * Write a PWM (analog) value to a pin.
     * Block implementation for "Write PWM Pin [PIN] [VALUE]%".
     * @param {object} args - Block arguments
     * @param {string} args.PIN - Pin number to write to
     * @param {string} args.VALUE - PWM value as percentage (0-100)
     */
    async pwm_write(args) {
        // Ensure connection is established
        if (!this.connected && !this.connection_pending) {
            await this.connect();
        }

        // Queue command if not connected yet
        if (!this.connected) {
            const callbackEntry = [this.pwm_write.bind(this), args];
            this.wait_open.push(callbackEntry);
        } else {
            // Parse arguments and convert percentage to 8-bit value
            const pin = parseInt(args["PIN"], 10);
            const percentage = parseInt(args["VALUE"], 10);
            const value = Math.round(
                CONFIG.PWM_MAX_VALUE *
                    (percentage / CONFIG.PWM_PERCENTAGE_SCALE)
            );

            // Set pin mode to PWM if not already set
            if (this.pin_modes[pin] !== PWM) {
                this.pin_modes[pin] = PWM;
                this.sendSerial(
                    JSON.stringify({ command: "set_mode_pwm", pin })
                );
            }

            // Send PWM write command
            this.sendSerial(
                JSON.stringify({ command: "pwm_write", pin, value })
            );
        }
    }

    /**
     * Play a tone on a pin for a specified duration.
     * Block implementation for "Tone Pin [PIN] [FREQ] Hz [DURATION] ms".
     * @param {object} args - Block arguments
     * @param {string} args.PIN - Pin number to play tone on
     * @param {string} args.FREQ - Frequency in Hz
     * @param {string} args.DURATION - Duration in milliseconds (max 5000ms)
     */
    async tone_on(args) {
        // Ensure connection is established
        if (!this.connected && !this.connection_pending) {
            await this.connect();
        }

        // Queue command if not connected yet
        if (!this.connected) {
            const callbackEntry = [this.tone_on.bind(this), args];
            this.wait_open.push(callbackEntry);
        } else {
            // Parse arguments with safety limits
            const pin = parseInt(args["PIN"], 10);
            const freq = parseInt(args["FREQ"], 10);
            const duration = Math.min(
                parseInt(args["DURATION"], 10),
                CONFIG.MAX_TONE_DURATION
            );

            // Set pin mode to TONE if not already set
            if (this.pin_modes[pin] !== TONE) {
                this.pin_modes[pin] = TONE;
                this.sendSerial(
                    JSON.stringify({ command: "set_mode_tone", pin })
                );
            }

            // Send tone command
            this.sendSerial(
                JSON.stringify({ command: "play_tone", pin, freq, duration })
            );
        }
    }

    async servo(args) {
        if (!this.connected && !this.connection_pending) {
            await this.connect();
        }
        if (!this.connected) {
            const callbackEntry = [this.servo.bind(this), args];
            this.wait_open.push(callbackEntry);
        } else {
            const pin = parseInt(args["PIN"], 10);
            const angle = parseInt(args["ANGLE"], 10);

            if (this.pin_modes[pin] !== SERVO) {
                this.pin_modes[pin] = SERVO;
                this.sendSerial(
                    JSON.stringify({ command: "set_mode_servo", pin })
                );
            }
            this.sendSerial(
                JSON.stringify({
                    command: "servo_position",
                    pin,
                    position: angle,
                })
            );
        }
    }

    async analog_read(args) {
        if (!this.connected && !this.connection_pending) {
            await this.connect();
        }
        if (!this.connected) {
            const callbackEntry = [this.analog_read.bind(this), args];
            this.wait_open.push(callbackEntry);
        } else {
            const pin = parseInt(args["PIN"], 10);

            // Always send the command to get the latest value
            this.pin_modes[pin] = ANALOG_INPUT;
            await this.sendSerial(
                JSON.stringify({ command: "set_mode_analog_input", pin })
            );

            return this.analog_inputs[pin];
        }
    }

    async digital_read(args) {
        if (!this.connected && !this.connection_pending) {
            await this.connect();
        }
        if (!this.connected) {
            const callbackEntry = [this.digital_read.bind(this), args];
            this.wait_open.push(callbackEntry);
        } else {
            const pin = parseInt(args["PIN"], 10);

            // Always send the command to get the latest value
            this.pin_modes[pin] = DIGITAL_INPUT;
            await this.sendSerial(
                JSON.stringify({ command: "set_mode_digital_input", pin })
            );

            return this.digital_inputs[pin];
        }
    }

    async sonar_read(args) {
        if (!this.connected && !this.connection_pending) {
            await this.connect();
        }
        if (!this.connected) {
            const callbackEntry = [this.sonar_read.bind(this), args];
            this.wait_open.push(callbackEntry);
        } else {
            const triggerPin = parseInt(args["TRIGGER_PIN"], 10);
            const echoPin = parseInt(args["ECHO_PIN"], 10);
            this.sonar_report_pin = triggerPin;

            // Always send the command to get the latest value
            this.pin_modes[triggerPin] = SONAR;
            await this.sendSerial(
                JSON.stringify({
                    command: "set_mode_sonar",
                    trigger_pin: triggerPin,
                    echo_pin: echoPin,
                })
            );

            return this.digital_inputs[this.sonar_report_pin];
        }
    }

    _setLocale() {
        let nowLocale = "";
        switch (formatMessage.setup().locale) {
            case "pt-br":
            case "pt":
                nowLocale = "pt-br";
                break;
            case "en":
                nowLocale = "en";
                break;
            case "fr":
                nowLocale = "fr";
                break;
            case "zh-tw":
                nowLocale = "zh-tw";
                break;
            case "zh-cn":
                nowLocale = "zh-cn";
                break;
            case "pl":
                nowLocale = "pl";
                break;
            case "ja":
                nowLocale = "ja";
                break;
            case "de":
                nowLocale = "de";
                break;
            case "nl":
                nowLocale = "nl";
                break;
            default:
                nowLocale = "en";
                break;
        }
        return nowLocale;
    }

    async connect() {
        console.log("Attempting to connect to Arduino via Web Serial API...");

        if (this.connected || this.connection_pending) {
            console.log(
                "Already connected or connecting, ignoring connection attempt"
            );
            return;
        }

        this.connection_pending = true;

        try {
            this.serialPort = await navigator.serial.requestPort();
            await this.serialPort.open({ baudRate: 57600 });

            console.log("Serial port opened successfully");

            this.writer = this.serialPort.writable.getWriter();

            // SET CONNECTED TO TRUE **BEFORE** await this.startReadLoop()
            this.connected = true;
            this.connect_attempt = true;
            this.alerted = false;

            // Now start the read loop (don't await it, just start it)
            this.startReadLoop();

            // Process any queued operations
            for (let index = 0; index < this.wait_open.length; index++) {
                const data = this.wait_open[index];
                await data[0](data[1]);
            }
            this.wait_open = [];
        } catch (error) {
            console.error("Error connecting to Arduino:", error);
            if (
                !(
                    error instanceof DOMException &&
                    error.name === "NotFoundError"
                )
            ) {
                if (!this.alerted) {
                    this.alerted = true;
                    alert(
                        "Could not connect to Arduino. Make sure it's plugged in, the correct port is selected, and the custom sketch is uploaded."
                    );
                }
            }
        } finally {
            this.connection_pending = false;
        }
    }

    async sendSerial(data) {
        console.log("sendSerial called with:", data);
        if (!this.writer) {
            console.error("Writer is not available, cannot send data!");
            return;
        }

        try {
            const encoder = new TextEncoder();
            const dataArrayBuffer = encoder.encode(data + "\n");
            await this.writer.write(dataArrayBuffer);
            console.log("Sent to Arduino:", data);
        } catch (error) {
            console.error("Error sending data:", error);
            this.handleDisconnect();
        }
    }

    async startReadLoop() {
        if (this.readLoopActive) return;
        this.readLoopActive = true;

        this.reader = this.serialPort.readable.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
            while (this.readLoopActive) {
                const { value, done } = await this.reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value);

                let endIndex;
                while ((endIndex = buffer.indexOf("\n")) !== -1) {
                    const message = buffer.slice(0, endIndex);
                    buffer = buffer.slice(endIndex + 1);

                    if (message.trim()) {
                        this.processSerialMessage(message);
                    }
                }
            }
        } catch (error) {
            console.error("Error reading data:", error);
        } finally {
            this.reader.releaseLock();
            this.readLoopActive = false;
        }
    }

    processSerialMessage(messageText) {
        try {
            const msg = JSON.parse(messageText);
            const reportType = msg["report"];
            let pin = null;
            let value = null;

            if (reportType === "digital_input") {
                pin = parseInt(msg["pin"], 10);
                value = msg["value"];
                this.digital_inputs[pin] = value;
            } else if (reportType === "analog_input") {
                pin = parseInt(msg["pin"], 10);
                value = msg["value"];
                this.analog_inputs[pin] = value;
            } else if (reportType === "sonar_data") {
                value = msg["value"];
                this.digital_inputs[this.sonar_report_pin] = value;
            }
        } catch (error) {
            console.error("Error processing message:", error, messageText);
        }
    }

    async handleDisconnect() {
        console.log("Disconnected!"); // Add this
        if (!this.connected) return;

        try {
            if (this.reader) {
                this.readLoopActive = false;
                await this.reader.cancel();
                this.reader = null;
            }

            if (this.writer) {
                this.writer.releaseLock();
                this.writer = null;
            }

            if (this.serialPort && this.serialPort.readable) {
                await this.serialPort.close();
            }

            this.serialPort = null;
        } catch (error) {
            console.error("Error during disconnect:", error);
        } finally {
            this.digital_inputs.fill(0);
            this.analog_inputs.fill(0);
            this.pin_modes.fill(-1);
            this.connected = false;

            console.log("Disconnected from Arduino");
        }
    }
}

module.exports = Scratch3ArduinoOneGPIO;
